import { useState, type FormEvent } from 'react';
import { Building2, Contact, MapPinned, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import customerService, { type CreateCustomerData } from '../../../../../services/customerService';
import SovereignModal from '../components/SovereignModal';
import { FieldLabel, PRIMARY_BTN, svInput, svSecondaryBtn } from '../ui';

function GroupLabel({ icon: Icon, children }: { icon: typeof Building2; children: string }) {
  return (
    <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#0B3B2E]">
      <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
      {children}
    </p>
  );
}

export default function AddCustomerModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: { _id: string; name: string; code?: string }) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [cName, setCName] = useState('');
  const [cCode, setCCode] = useState('');
  const [cType, setCType] = useState<CreateCustomerData['customerType']>('Pvt Ltd');
  const [cEmail, setCEmail] = useState('');
  const [cMobile, setCMobile] = useState('');
  const [cPhone, setCPhone] = useState('');
  const [cStreet, setCStreet] = useState('');
  const [cCity, setCCity] = useState('');
  const [cState, setCState] = useState('');
  const [cCountry, setCCountry] = useState('India');
  const [cPincode, setCPincode] = useState('');

  const reset = () => {
    setCName(''); setCCode(''); setCType('Pvt Ltd');
    setCEmail(''); setCMobile(''); setCPhone('');
    setCStreet(''); setCCity(''); setCState(''); setCCountry('India'); setCPincode('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!cMobile.trim()) {
      toast.error('Mobile number is required');
      return;
    }
    setSaving(true);
    try {
      const res = await customerService.createCustomer({
        name: cName.trim(),
        code: cCode.trim().toUpperCase(),
        customerType: cType,
        email: cEmail.trim(),
        phone: cPhone.trim() || '',
        mobile: cMobile.trim(),
        address: {
          street: cStreet.trim(),
          city: cCity.trim(),
          state: cState.trim(),
          country: cCountry.trim() || 'India',
          pincode: cPincode.trim(),
        },
      });
      toast.success('Customer created');
      onCreated({ _id: res.data._id, name: res.data.name, code: res.data.code });
      reset();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not create customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SovereignModal
      open={open}
      onClose={handleClose}
      title="Add new customer"
      subtitle="Registered company details for this project"
      icon={Building2}
      maxWidthClass="max-w-3xl"
      footer={
        <>
          <button type="button" onClick={handleClose} className="rounded-lg px-4 py-2 text-sm font-semibold transition hover:opacity-90" style={svSecondaryBtn}>
            Cancel
          </button>
          <button
            type="submit"
            form="add-customer-form"
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
            style={PRIMARY_BTN}
          >
            {saving ? 'Saving…' : (<><Plus className="h-4 w-4" strokeWidth={2.5} />Save customer</>)}
          </button>
        </>
      }
    >
      <form id="add-customer-form" onSubmit={(e) => void handleSave(e)} className="space-y-6">
        <section className="space-y-3">
          <GroupLabel icon={Building2}>Company</GroupLabel>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel hint="Required">Customer name</FieldLabel>
              <input value={cName} onChange={(e) => setCName(e.target.value)} required className={svInput} placeholder="Company / person name" maxLength={300} />
            </div>
            <div>
              <FieldLabel hint="Required">Customer code</FieldLabel>
              <input value={cCode} onChange={(e) => setCCode(e.target.value.toUpperCase())} required className={svInput} placeholder="e.g. CUST-01" maxLength={20} />
            </div>
          </div>
          <div className="sm:w-1/2">
            <FieldLabel hint="Required">Customer type</FieldLabel>
            <select value={cType} onChange={(e) => setCType(e.target.value as CreateCustomerData['customerType'])} className={svInput}>
              {(['LLP', 'Pvt Ltd', 'Proprietor', 'Public Ltd', 'Partnership', 'Sole Proprietorship', 'Other'] as const).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </section>

        <section className="space-y-3">
          <GroupLabel icon={Contact}>Contact</GroupLabel>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <FieldLabel hint="Required">Email</FieldLabel>
              <input type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} required className={svInput} placeholder="contact@company.com" />
            </div>
            <div>
              <FieldLabel hint="Required">Mobile</FieldLabel>
              <input type="tel" value={cMobile} onChange={(e) => setCMobile(e.target.value)} required className={svInput} placeholder="+91 98765 43210" />
            </div>
            <div>
              <FieldLabel hint="Optional">Phone / Landline</FieldLabel>
              <input type="tel" value={cPhone} onChange={(e) => setCPhone(e.target.value)} className={svInput} placeholder="022-XXXX XXXX" />
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <GroupLabel icon={MapPinned}>Registered address</GroupLabel>
          <div>
            <FieldLabel hint="Optional">Street address</FieldLabel>
            <input value={cStreet} onChange={(e) => setCStreet(e.target.value)} className={svInput} placeholder="Street / locality" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel hint="Optional">City</FieldLabel>
              <input value={cCity} onChange={(e) => setCCity(e.target.value)} className={svInput} placeholder="City" />
            </div>
            <div>
              <FieldLabel hint="Optional">State</FieldLabel>
              <input value={cState} onChange={(e) => setCState(e.target.value)} className={svInput} placeholder="State" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel hint="Optional">Country</FieldLabel>
              <input value={cCountry} onChange={(e) => setCCountry(e.target.value)} className={svInput} placeholder="Country" />
            </div>
            <div>
              <FieldLabel hint="Optional">Pincode</FieldLabel>
              <input value={cPincode} onChange={(e) => setCPincode(e.target.value.replace(/\D/g, ''))} className={svInput} placeholder="6-digit pincode" maxLength={6} />
            </div>
          </div>
        </section>
      </form>
    </SovereignModal>
  );
}
